import { useState } from 'react';
import { CalendarDays, FileDown, FileText, TableProperties } from 'lucide-react';
import { toast } from 'sonner';
import type { DateRange } from 'react-day-picker';

import { ExportMenu, type ExportFormat } from './ExportMenu';

import {
  copyStatsToClipboard,
  SHEETS_HINT_DURATION_MS,
  SHEETS_OPEN_DELAY_MS,
  SHEETS_URL,
} from '../lib/export-sheets';
import { SheetsClipboardHint } from './SheetsClipboardHint';

import type { MediaStats } from '../types/statistics';
import type { ReportPage } from '../lib/export-docs';

type ExportToolbarProps = {
  data: MediaStats[];
  onExportPdf: () => void;
  dateRange: DateRange | undefined;
  pages: string[][][];
};

function buildReportPages(pages: string[][][], results: MediaStats[]): ReportPage[] {
  const byUrl = new Map(results.map(r => [r.url, r]));

  if (pages.length === 0) {
    return [{ posts: results, imagePost1: results[0], imagePost2: results[1] }];
  }

  return pages
    .map(subGroups => {
      const posts = subGroups.flat().flatMap(url => (byUrl.has(url) ? [byUrl.get(url)!] : []));
      return { posts, imagePost1: posts[0], imagePost2: posts[1] };
    })
    .filter(p => p.posts.length > 0);
}

export function ExportToolbar({ data, onExportPdf, dateRange, pages }: ExportToolbarProps): JSX.Element {
  const [sheetsCopied, setSheetsCopied] = useState(false);
  const [isGeneratingSheets, setIsGeneratingSheets] = useState(false);
  const [isGeneratingDoc, setIsGeneratingDoc] = useState(false);
  const [isGeneratingMonthly, setIsGeneratingMonthly] = useState(false);
  const [isGeneratingDaily, setIsGeneratingDaily] = useState(false);

  async function handleExportSheets(): Promise<void> {
    try {
      await copyStatsToClipboard(data);
      setSheetsCopied(true);
      setTimeout(() => {
        window.open(SHEETS_URL, '_blank', 'noopener,noreferrer');
      }, SHEETS_OPEN_DELAY_MS);
      setTimeout(() => setSheetsCopied(false), SHEETS_HINT_DURATION_MS);
    } catch {
      toast.error('Не удалось скопировать данные в буфер обмена');
    }
  }

  async function handleExportMondayReport(): Promise<void> {
    setIsGeneratingSheets(true);
    try {
      const { generateGoogleSheetsReport } = await import('../lib/export-sheets');
      const reportPages = buildReportPages(pages, data);
      await generateGoogleSheetsReport(reportPages);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Не удалось создать таблицу');
    } finally {
      setIsGeneratingSheets(false);
    }
  }

  async function handleExportMonthlyReport(): Promise<void> {
    setIsGeneratingMonthly(true);
    try {
      const { generateMonthlyReport } = await import('../lib/export-monthly');
      const reportPages = buildReportPages(pages, data);
      await generateMonthlyReport(reportPages);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Не удалось создать отчёт');
    } finally {
      setIsGeneratingMonthly(false);
    }
  }

  async function handleDailyReport(): Promise<void> {
    setIsGeneratingDaily(true);
    try {
      const { generateDailyReport } = await import('../lib/export-daily');
      const reportPages = buildReportPages(pages, data);
      await generateDailyReport(reportPages);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Не удалось создать отчёт');
    } finally {
      setIsGeneratingDaily(false);
    }
  }

  async function handleGenerateDoc(): Promise<void> {
    setIsGeneratingDoc(true);
    try {
      const { generateGoogleDocsReport } = await import('../lib/export-docs');
      const reportPages = buildReportPages(pages, data);
      const { goalError } = await generateGoogleDocsReport(reportPages, dateRange);
      if (goalError) {
        toast.warning(`Отчёт создан без целей: ${goalError}`, { duration: 15000 });
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Не удалось создать документ');
    } finally {
      setIsGeneratingDoc(false);
    }
  }

  const busy = isGeneratingSheets || isGeneratingMonthly || isGeneratingDaily || isGeneratingDoc;
  const pagesNote = pages.length > 1 ? `, страниц: ${pages.length}` : '';

  // Шесть кнопок в ряд не помещались и не объясняли, чем отличаются, —
  // теперь одно меню с подписью под каждым вариантом.
  const formats: ExportFormat[] = [
    { id: 'pdf', label: 'PDF', description: 'Таблица как на экране', icon: FileDown },
    {
      id: 'sheets',
      label: 'Google Таблицы',
      description: 'Скопировать таблицу и открыть пустую Google Таблицу — вставить Ctrl+V',
      icon: TableProperties,
      disabled: sheetsCopied,
    },
    {
      id: 'monday',
      label: 'Отчёт по понедельникам',
      description: `Google Таблица по образцу еженедельного отчёта${pagesNote}`,
      icon: TableProperties,
    },
    {
      id: 'monthly',
      label: 'Ежемесячный отчёт',
      description: `Google Таблица по образцу ежемесячного отчёта${pagesNote}`,
      icon: TableProperties,
    },
    {
      id: 'docs',
      label: 'Отчёт в Google Docs',
      description: dateRange?.from
        ? 'Документ с цифрами и целями за выбранный период'
        : 'Документ с цифрами и целями — период не выбран, в шапке его не будет',
      icon: FileText,
    },
    { id: 'daily', label: 'Ежедневный отчёт', description: 'Google Docs по образцу «ОТРАБОТАНО Instagram ✅»: топ видео', icon: CalendarDays },
  ];

  function handleSelect(id: string): void {
    if (id === 'pdf') onExportPdf();
    else if (id === 'sheets') void handleExportSheets();
    else if (id === 'monday') void handleExportMondayReport();
    else if (id === 'monthly') void handleExportMonthlyReport();
    else if (id === 'docs') void handleGenerateDoc();
    else if (id === 'daily') void handleDailyReport();
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <ExportMenu
          formats={formats}
          onSelect={handleSelect}
          label="Выгрузить"
          variant="outline"
          size="sm"
          busy={busy}
          busyLabel="Создание..."
        />
      </div>

      {sheetsCopied && <SheetsClipboardHint />}
    </div>
  );
}
